import React, { Component } from "react";
import { Col, Row, Select, Table, notification } from "antd";
import { ContentWrapper, Icon } from "components";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { formatPrice } from "utils/utils";
import moment from "moment";
import { push } from "connected-react-router";
import { Area } from "@ant-design/charts";
import "./styles.scss";
import xlsx from "json-as-xlsx";

class Wallet extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			data: [],
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month"),
			partner: undefined,
			partnerOptions: [],
			partnerSearch: ""
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { startDate, endDate, partner } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		const body = {
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString(),
			partner
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("wallet/search"),
				data: body
			});

			if (response.ok) {
				const { movements = [], cards, commissionsChart } = response.data.results;

				this.setState({
					data: movements,
					cards,
					commissionsChart
				});
			} else {
				notification.error({
					message: strings.sidebar.wallet,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSearch(partner: any) {
		this.setState({ partnerSearch: partner });

		if (partner.length < 3) return;

		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {
			this.getUsers();
		}, 500);
	}

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: strings.sidebar.wallet,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.sidebar.wallet,
							route: "/partner-wallet",
							icon: "wallet"
						}
					],
					actions: [
						{
							type: "select",
							text: strings.fields.partner,
							placeholder: strings.generic.searchByEmailOrName,
							options: partnerOptions.map((partner: any) => ({
								value: partner._id,
								text: `${partner.name} (${partner.email})`
							})),
							onChange: (partner: any) => {
								this.setState({ partner }, () => this.getData());
							},
							value: this.state.partner,
							showSearch: true,
							onSearch: (value: string) => this.handleSearch(value),
							separator: "right",
							style: { width: 300, height: 40 },
							allowClear: true
						},
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	getType(movement: any) {
		const { dispatch } = this.props;

		switch (movement.movementType) {
			case "spentCommission":
				return (
					<span>
						<span>{strings.wallet.balanceUsed}</span>
						&nbsp;
						<a
							href={`/partner-orders/${movement.order?._id}`}
							onClick={(e) => {
								e.preventDefault();
								dispatch(push(`/partner-orders/${movement.order?._id}`));
							}}
						>
							#{movement.order?.orderNumber}
						</a>
					</span>
				);
			case "expiredCommission":
				return <span>{strings.wallet.expiredCommission}</span>;
			case "adjustment":
				return <span>{strings.wallet.adjustment}</span>;
			case "redeem":
				if (movement.order) {
					return (
						<span>
							{strings.formatString(
								strings.wallet.redeemFrom,
								movement.type === "sales"
									? (strings.orders.sales as string)?.toLowerCase()
									: (strings.generic.bonuses as string)?.toLowerCase()
							)}
							&nbsp;
							<span>
								({movement.notes} -{" "}
								<a
									href={`/orders/${movement.order?._id}`}
									onClick={(e) => {
										e.preventDefault();
										dispatch(push(`/orders/${movement.order?._id}`));
									}}
								>
									#{movement.order?.orderNumber}
								</a>
								)
							</span>
						</span>
					);
				}

				return (
					<span>
						{strings.formatString(
							strings.wallet.redeemFrom,
							movement.type === "sales"
								? (strings.orders.sales as string)?.toLowerCase()
								: (strings.generic.bonuses as string)?.toLowerCase()
						)}
						{movement.notes ? ` (${movement.notes})` : ""}
					</span>
				);
			default:
				return movement.movementType;
		}
	}

	getTypeString(movement: any) {
		switch (movement.movementType) {
			case "spentCommission":
				return strings.wallet.balanceUsed;
			case "expiredCommission":
				return strings.wallet.expiredCommission;
			case "adjustment":
				return strings.wallet.adjustment;
			case "redeem":
				return strings.wallet.redeem;
			default:
				return movement.movementType;
		}
	}

	renderNumber(value: number, isExpired?: boolean) {
		if (value > 0) {
			return <span className="WalletValueGreen">{formatPrice(value)}</span>;
		} else if (value < 0) {
			return <span className="WalletValueRed">{isExpired ? formatPrice(Math.abs(value)) : formatPrice(value)}</span>;
		}

		return <span>{formatPrice(value)}</span>;
	}

	exportMovementsToExcel = async () => {
		const { dispatch } = this.props;
		const { data: movements } = this.state;

		dispatch(setLoader(true));

		const content = movements.map((s: any) => ({
			date: DateTime.fromISO(s._created).toFormat("dd/MM/yyyy HH:mm"),
			notes: s.notes,
			partner: `${s.partner.name} (${s.partner.email})`,
			type: this.getTypeString(s),
			value:
				s.movementType === "expiredCommission" || s.movementType === "spentCommission"
					? -s.balance
					: s.movementType === "adjustment"
					? s.balance
					: s.value
		}));

		const data = [
			{
				sheet: "Partners",
				columns: [
					{ label: strings.fields.date, value: "date" },
					{ label: strings.fields.description, value: "notes" },
					{ label: strings.fields.partner, value: "partner" },
					{ label: strings.settings.type, value: "type" },
					{ label: strings.settings.value, value: "value" }
				],
				content: content
			}
		];

		const settings = {
			sheetName: "Customers",
			extraLength: 3,
			writeMode: "writeFile",
			writeOptions: {}
		};

		xlsx(data, settings);

		dispatch(setLoader(false));
	};

	renderMovements() {
		const { data = [], partner } = this.state;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.wallet.movements}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>
							{strings.formatString(
								strings.wallet.earnedPeriod,
								formatPrice(
									data.reduce(
										(acc: number, entry: any) => acc + (entry.movementType === "redeem" ? entry.value || 0 : 0),
										0
									)
								)
							)}
						</p>
						<button className="EthicAddButton" onClick={this.exportMovementsToExcel}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "_created",
							key: "_created",
							render: (_created: string) => DateTime.fromISO(_created).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
							width: 150
						},
						{
							title: strings.fields.description,
							dataIndex: "description",
							key: "description",
							render: (description: string, record: any) => this.getType(record),
							width: 200
						},
						{
							title: strings.fields.partner,
							dataIndex: "partner",
							key: "partner",
							render: (partner: any) => (partner ? `${partner.name} (${partner.email})` : "-"),
							sorter: (a, b) => a.name?.localCompare(b.name),
							width: 100
						},
						{
							title: strings.settings.value,
							dataIndex: "value",
							key: "value",
							render: (value: number, record: any) => {
								let val =
									record.movementType === "expiredCommission" || record.movementType === "spentCommission"
										? -record.balance
										: value;

								if (record.movementType === "adjustment") {
									val = record.balance;
								}

								return this.renderNumber(val, record.movementType === "expiredCommission");
							},
							sorter: (a, b) => a.value - b.value,
							width: 80
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (expirationDate: string) => DateTime.fromISO(expirationDate).toFormat("dd/MM/yyyy"),
							sorter: (a, b) => DateTime.fromISO(a.expirationDate).toMillis() - DateTime.fromISO(b.expirationDate).toMillis(),
							width: 150
						}
					]}
					dataSource={data}
				/>
			</ContentWrapper>
		);
	}

	renderChart() {
		const { commissionsChart = [] } = this.state;

		const config = {
			data: commissionsChart,
			xField: "month",
			yField: "total",
			appendPadding: 10,
			smooth: true,
			animation: {
				appear: {
					animation: "path-in",
					duration: 2500
				}
			},
			meta: {
				total: {
					alias: strings.wallet.totalBilled
				}
			},
			tooltip: {
				formatter: (datum: any) => {
					return { name: strings.commissions.gainedBalance, value: formatPrice(datum.total) };
				}
			}
		};

		return (
			<ContentWrapper extraStyle={{ marginBottom: 0 }}>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.wallet.monthlyControl}</h2>
					</div>
				</div>
				<Area style={{ marginTop: 20 }} {...config} />
			</ContentWrapper>
		);
	}

	render() {
		const { cards } = this.state;
		const { available = 0, spent = 0, expired = 0, total } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: total,
				description: strings.wallet.gainedBalance,
				type: "currency"
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return (
			<div className="Wallet">
				<Helmet>
					<title>{strings.sidebar.wallet}</title>
					<meta name="description" content="Description of Wallet" />
				</Helmet>
				<div className="IconCards">
					<Row gutter={[20, 20]}>
						{CARDS.map((card, index) => (
							<Col xs={24} md={6} key={index}>
								<div className="IconCardOuterContainer">
									<div className="IconCard">
										<div className="IconCardHeader">
											<div className="IconCardHeaderMask" />
											<div className="IconCardType">
												<Icon name={card.icon} />
											</div>
										</div>
										<div className="IconCardBody">
											<span className="IconCardValue">
												{card.type === "currency" ? formatPrice(card.value) : card.value}
											</span>
											<h3 className="IconCardDescription">{card.description}</h3>
										</div>
									</div>
								</div>
							</Col>
						))}
						<Col xs={24}>{this.renderChart()}</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(Wallet);
